import _keyBy from 'lodash/keyBy';
import _get from 'lodash/get';
import differenceInWeeks from 'date-fns/differenceInWeeks';

import http from '@homeservices/api-kit/axios';

import { getPRWorkOrders } from '@homeservices/api-kit/workorder/proreferral';
import { NEW_MATCH_STATUSES } from '@/constants/match-statuses';

import {
  getServiceProviderAttributeValues,
  getServiceProviderContacts,
} from '@homeservices/api-kit/serviceprovider';
import { getServiceProviders } from '@homeservices/api-kit/search/serviceprovider/v1';

import {
  getOccupations as getServiceProviderOccupations,
} from '@homeservices/api-kit/serviceprovider/occupations';

import {
  getOccupations as getServiceCatalogOccupations,
} from '@homeservices/api-kit/servicecatalog/occupations';

import info from './modules/info';
import education from './modules/education';
import unavailabilities from './modules/unavailabilities';
import inbox from './modules/inbox';
import pricing from './modules/pricing';

const UNAVAILABLE_OCCUPATION_STATUSES = [
  'archived',
  'terminated',
];

export default {
  namespaced: true,
  modules: {
    info,
    education,
    serviceProviderUnavailabilities: unavailabilities,
    inbox,
    pricing,
  },
  state () {
    return {
      meta: {
        fetching: false,
      },
      serviceProviders: {},
      serviceProviderAttributeValues: {},
      serviceProviderOccupations: {},
      serviceCatalogOccupations: {},
      performance: {},
      matchStatuses: {},
    };
  },
  getters: {
    isFetching (state) {
      return state.meta.fetching;
    },
    isNewPro ({ serviceProviderAttributeValues }) {
      const proSinceDate = _get(serviceProviderAttributeValues, 'pro_since.value');
      return proSinceDate ? differenceInWeeks(new Date(), new Date(proSinceDate)) < 2 : false;
    },
    serviceProvidersIds ({ serviceProviders }) {
      return Object.keys(serviceProviders);
    },
    serviceProvider ({ serviceProviders }) {
      return Object.values(serviceProviders)[0];
    },
    serviceProviderOccupationIds ({ serviceProviderOccupations }) {
      return Object.values(serviceProviderOccupations)
        .map(serviceProviderOccupation => serviceProviderOccupation.occupation_id);
    },
    // Merge specific occupation details with generic occupation details
    serviceProviderCatalogOccupations ({ serviceProviderOccupations, serviceCatalogOccupations }) {
      return _keyBy(Object.values(serviceProviderOccupations).map((serviceProviderOccupation) => {
        return {
          ...serviceProviderOccupation,
          occupation: serviceCatalogOccupations[serviceProviderOccupation.occupation_id] || {},
        };
      }), 'id');
    },
    occupationNames ({ serviceCatalogOccupations }) {
      return Object.values(serviceCatalogOccupations)
        .map(catalogOccupation => catalogOccupation.display_name);
    },
    hasTakenOneTimeSurvey ({ serviceProviderAttributeValues }) {
      const hasTakenSurvey = _get(serviceProviderAttributeValues, 'monthly_lead_quantity_survey_submitted.value') || '';
      return hasTakenSurvey.toLowerCase() === 'yes';
    },
    hiredCount ({ matchStatuses }) {
      return matchStatuses.hired || 0;
    },
    potentialCount ({ matchStatuses }) {
      return matchStatuses.potential || 0;
    },
    rejectedCount ({ matchStatuses }) {
      return matchStatuses.rejected || 0;
    },
    unrepliedCount ({ matchStatuses }) {
      return matchStatuses.unreplied || 0;
    },
    cancelledCount ({ matchStatuses }) {
      return matchStatuses.cancelled || 0;
    },
    completedCount ({ matchStatuses }) {
      return matchStatuses.completed || 0;
    },
    unresponsiveCount ({ matchStatuses }) {
      return matchStatuses.unresponsive || 0;
    },
    closedCount ({ matchStatuses }) {
      return matchStatuses.closed || 0;
    },
    acceptedCount ({ matchStatuses }) {
      return matchStatuses.accepted || 0;
    },
    performance30 ({ performance }) {
      return performance.leads_metrics['30_days_time_interval_leads'] || {};
    },
    performance60 ({ performance }) {
      return performance.leads_metrics['60_days_time_interval_leads'] || {};
    },
    performance90 ({ performance }) {
      return performance.leads_metrics['90_days_time_interval_leads'] || {};
    },
    topPro ({ performance }) {
      return performance.top_pro;
    },
  },
  actions: {
    // Fetch service providers and occupations/attributes
    async fetch ({ commit, dispatch, getters }) { // TODO: Rename it to be more descriptive
      commit('meta', { fetching: true });

      try {
        await dispatch('fetchServiceProviders');
        await Promise.all([
          dispatch('fetchServiceProviderContacts', getters.serviceProvidersIds),
          dispatch('fetchServiceProviderAttributeValues', getters.serviceProvidersIds),
          dispatch('fetchServiceProviderOccupations', getters.serviceProvidersIds),
          dispatch('fetchServiceProviderUnavailabilities'),
        ]);
        await dispatch('fetchServiceCatalogOccupations', getters.serviceProviderOccupationIds);
      } catch (error) {
        commit('meta', { fetching: false });
      }

      commit('meta', { fetching: false });
    },
    async fetchServiceProviders ({ commit }) {
      const { data } = await getServiceProviders();
      commit('setServiceProviders', _keyBy(data.results, 'id'));
    },
    async fetchServiceProviderContacts ({ commit }, ids) {
      const { data } = await getServiceProviderContacts({ service_provider_ids: ids.join(',') });
      commit('info/setUserContacts', _keyBy(data.results, 'contact_type'));
    },
    async fetchServiceProviderAttributeValues ({ commit }, ids) {
      const { data } = await getServiceProviderAttributeValues({ service_provider_ids: ids.join(',') });
      commit('setServiceProviderAttributeValues', _keyBy(data.results, 'service_provider_attribute'));
    },
    async fetchServiceProviderOccupations ({ commit }, ids) {
      const { data } = await getServiceProviderOccupations({
        service_provider_ids: ids.join(','),
        lifecycle_statuses: 'active',
      });

      // remove any value that have status set to "terminated". api doesn't support this kind of
      // filtering so we just do it here.
      const results = data.results.filter((s) => {
        return !UNAVAILABLE_OCCUPATION_STATUSES.includes(s.service_provider_occupation_status);
      });

      commit('setServiceProviderOccupations', _keyBy(results, 'id'));
    },
    async fetchServiceCatalogOccupations ({ commit, dispatch }, ids) {
      const { data } = await getServiceCatalogOccupations({ occupation_ids: ids.join(',') });
      commit('setServiceCatalogOccupations', _keyBy(data.results, 'id'));
      dispatch('fetchPerformance');
      dispatch('fetchMatchStatuses');
    },
    async fetchPerformance ({ commit, getters }) {
      try {
        const { data: { results: [result] } } = await http.get('/platform/coresearch/api/performance/v1', {
          params: {
            account_id: getters.serviceProvider.identifier,
            is_time_interval: true,
          },
        });
        commit('setPerformance', result);
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    async fetchMatchStatuses ({ commit, getters }) {
      const params = {
        size: 0,
        from: 0,
        service_provider: getters.serviceProvider.id,
        match_status: Object.values(NEW_MATCH_STATUSES).filter(status => status !== 'matched').join(','),
        with_aggregation: true,
        sort: 'time_created desc',
      };
      try {
        const { data: { aggregation: { match_statuses } } } = await getPRWorkOrders(params);
        commit('setMatchStatuses', match_statuses);
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    async updatePendo ({ getters, rootGetters, dispatch }, type) {
      await dispatch('fetch');
      await dispatch('fetchPerformance');
      const status = getters.serviceProvider.service_provider_status;
      if (!rootGetters['loginspoof/isLDAPLoggedIn']) {
        const occupations = getters.occupationNames;
        const newCount = getters.acceptedCount + getters.unrepliedCount;
        const archive = getters.closedCount
          + getters.rejectedCount
          + getters.cancelledCount
          + getters.unresponsiveCount;

        const loginEmail = rootGetters['account/pro/info/email'];
        const visitor = getters.occupationNames.length
          ? {
            id: getters.serviceProvider.identifier,
            role: 'Pro',
            email: loginEmail,
            service_provider_status: status,
            account_created_date: getters.serviceProvider.created_dtm,
            occupations,
            new: newCount,
            potential: getters.potentialCount,
            hired: getters.hiredCount,
            completed: getters.completedCount,
            archive,
            response_rate_30: getters.performance30.response_rate,
            response_rate_60: getters.performance60.response_rate,
            response_rate_90: getters.performance90.response_rate,
            acceptance_rate_30: getters.performance30.acceptance_rate,
            acceptance_rate_60: getters.performance60.acceptance_rate,
            acceptance_rate_90: getters.performance90.acceptance_rate,
            completed_jobs_30: getters.performance30.leads_completed,
            completed_jobs_60: getters.performance60.leads_completed,
            completed_jobs_90: getters.performance90.leads_completed,
            top_pro: getters.topPro,

          }
          : {
            id: getters.userId,
            role: 'Pro',
            email: loginEmail,
            service_provider_status: status,
            account_created_date: getters.serviceProvider.created_dtm,
            new: newCount,
            potential: getters.potentialCount,
            hired: getters.hiredCount,
            completed: getters.completedCount,
            archive,
            response_rate_30: getters.performance30.response_rate,
            response_rate_60: getters.performance60.response_rate,
            response_rate_90: getters.performance90.response_rate,
            acceptance_rate_30: getters.performance30.acceptance_rate,
            acceptance_rate_60: getters.performance60.acceptance_rate,
            acceptance_rate_90: getters.performance90.acceptance_rate,
            completed_jobs_30: getters.performance30.leads_completed,
            completed_jobs_60: getters.performance60.leads_completed,
            completed_jobs_90: getters.performance90.leads_completed,
            topPro: getters.topPro,
          };
        if (type === 'update') {
          window.pendo.updateOptions({ visitor });
        } else {
          window.pendo.initialize({ visitor });
        }
      }
    },
  },
  mutations: {
    meta (state, payload) {
      state.meta = {
        ...state.meta,
        ...payload,
      };
    },
    setServiceProviders (state, data) {
      state.serviceProviders = data;
    },
    setServiceProviderAttributeValues (state, data) {
      state.serviceProviderAttributeValues = data;
    },
    setServiceProviderOccupations (state, data) {
      state.serviceProviderOccupations = data;
    },
    setServiceCatalogOccupations (state, data) {
      state.serviceCatalogOccupations = data;
    },
    setPerformance (state, data) {
      state.performance = data;
    },
    setMatchStatuses (state, data) {
      state.matchStatuses = data;
    },
  },
};
